import {
  Investisseur,
  InvestisseurFilterInput,
  InvestisseursPaginatedCollectionSegment,
  PersonnePhysique,
  ProfilInvestisseurFiche,
  SituationPersonnelle,
} from '@lib/models/generated/graphql';
import { gql } from 'apollo-angular';
import { QueryManagerService } from 'finzzle-angular-utils';
import { firstValueFrom } from 'rxjs';

export const GET_INVESTISSEUR_PATRIMOINE = gql`
  query investisseursPaginated($where: InvestisseurFilterInput) {
    investisseursPaginated(skip: 0, take: 2, where: $where) {
      items {
        id
        personnePhysique {
          nom
          prenom
          dateNaissance
          etatCivil {
            typeSituationEtatCivilCodeNavigation {
              code
              libelle
            }
            typeRegimeSituationEtatCivilCodeNavigation {
              code
              libelle
            }
          }
          relationFamillePersonnePhysique1 {
            enfantACharge
          }
          situationProfessionnelle {
            profession
            categorieSocioProfessionnelleCode
          }
        }
      }
    }
  }
`;

export const GET_MINOTAURE_PROFIL_INVESTISSEUR = gql`
  query minotaureProfilInvestisseur($investisseurId: Long!) {
    minotaureProfilInvestisseur(investisseurId: $investisseurId) {
      profilQpiCode
      profilQpiLibelle
    }
  }
`;

export type ConnaissanceClientData = {
  investisseurId: number;
  situationPersonnelle?: SituationPersonnelle;
  profilInvestisseur?: ProfilInvestisseurFiche;
};

// Retrieves personal information and the investor risk profile for the fiche.
// It has only one public method : updateConnaissanceClientData
// and it's abstract to force the use of static methods

export abstract class ConnaissanceClient {
  static async updateConnaissanceClientData(
    queryManager: QueryManagerService,
    investisseurs: { investisseur?: Investisseur; coInvestisseur?: Investisseur }
  ) {
    const investisseur = investisseurs.investisseur;
    const coInvestisseur = investisseurs.coInvestisseur;

    const investisseurId = investisseur?.id;
    if (!investisseurId) {
      return;
    }
    const coInvestisseurId = coInvestisseur?.id;

    let where: InvestisseurFilterInput = { id: { eq: investisseurId } };
    if (coInvestisseurId) {
      const or = [{ id: { eq: investisseurId } }, { id: { eq: coInvestisseurId } }];
      where = { or };
    }
    const result = await firstValueFrom(
      queryManager.query<{ investisseursPaginated: InvestisseursPaginatedCollectionSegment }>({
        query: GET_INVESTISSEUR_PATRIMOINE,
        variables: { where },
        fetchPolicy: 'network-only',
      })
    );
    const dataResult = result.data?.investisseursPaginated;
    if (!dataResult || !dataResult.items) {
      return;
    }
    const investisseurForConnaissanceClient = dataResult.items.find(investisseur => investisseur.id === investisseurId);
    const coInvestisseurForConnaissanceClient = dataResult.items.find(
      investisseur => investisseur.id === coInvestisseurId
    );

    const updates: { investisseur?: Investisseur; coInvestisseur?: Investisseur } = {};

    if (investisseurForConnaissanceClient) {
      const inv = { ...investisseurForConnaissanceClient, ...investisseur };
      const connaissanceInvestisseur: ConnaissanceClientData | undefined =
        await this.getConnaissancClientFromInvestisseur(queryManager, inv);
      inv.profil = connaissanceInvestisseur?.profilInvestisseur;
      inv.situationPersonnelle = connaissanceInvestisseur?.situationPersonnelle;
      inv.personnePhysique = investisseurForConnaissanceClient.personnePhysique;
      inv.dateNaissance = investisseurForConnaissanceClient.personnePhysique?.[0]?.dateNaissance;
      updates.investisseur = inv;
    }
    if (coInvestisseurForConnaissanceClient) {
      const coInv = { ...coInvestisseurForConnaissanceClient, ...coInvestisseur };
      const connaissanceCoInvestisseur = await this.getConnaissancClientFromInvestisseur(
        queryManager,
        coInv
      );
      coInv.profil = connaissanceCoInvestisseur?.profilInvestisseur;
      coInv.situationPersonnelle = connaissanceCoInvestisseur?.situationPersonnelle;
      coInv.personnePhysique = coInvestisseurForConnaissanceClient.personnePhysique;
      coInv.dateNaissance = coInvestisseurForConnaissanceClient.personnePhysique?.[0]?.dateNaissance;
      updates.coInvestisseur = coInv;
    }
    if (!updates.investisseur) {
      return;
    }
    return updates as { investisseur: Investisseur; coInvestisseur?: Investisseur };
  }

  private static getMaritalStatusAndRegime(pp: PersonnePhysique): string {
    const etatCivil = pp.etatCivil;
    if (!etatCivil?.typeSituationEtatCivilCodeNavigation?.code) {
      return 'non renseigné';
    }
    const maritalStatus = etatCivil?.typeSituationEtatCivilCodeNavigation?.libelle ?? 'Non renseigné';
    const regime = etatCivil?.typeRegimeSituationEtatCivilCodeNavigation?.libelle ?? 'Non renseigné';
    const maritalCode = etatCivil?.typeSituationEtatCivilCodeNavigation?.code ?? 'Non renseigné';
    if (maritalCode === 'MARIE') {
      return `${maritalStatus} - ${regime}`;
    }

    return `${maritalStatus}`;
  }

  private static calculateEnfantACharge(pp: PersonnePhysique): number {
    return pp.relationFamillePersonnePhysique1?.filter(r => r.enfantACharge).length ?? 0;
  }



  private static getInfoPersonelleFromInvestisseur(inv: Investisseur): SituationPersonnelle | undefined {
    const pp = inv.personnePhysique[0];
    if (!pp) {
      return undefined;
    }
    return {
      enfantACharge: this.calculateEnfantACharge(inv.personnePhysique[0]),
      etatCivilTypeCode: pp.etatCivil?.typeSituationEtatCivilCodeNavigation?.code,
      etatCivilTypeLibelle: pp.etatCivil?.typeSituationEtatCivilCodeNavigation?.libelle,
      etatCivilTypeRegimeCode: pp.etatCivil?.typeRegimeSituationEtatCivilCodeNavigation?.code,
      etatCivilTypeRegimeLibelle: pp.etatCivil?.typeRegimeSituationEtatCivilCodeNavigation?.libelle,
      etatCivilDisplay: this.getMaritalStatusAndRegime(pp),
      profession: pp.situationProfessionnelle?.profession,
      categorieSocioProfessionnelleCode: pp.situationProfessionnelle?.categorieSocioProfessionnelleCode,
    };
  }



  //TODO: get the data for missing fields (experience and tolerance perte)
  // US23072 https://omnium-finance.visualstudio.com/ConseilPlacement/_workitems/edit/23072
  private static getProfilInvestisseurFromInvestisseur(
    inv: Investisseur,
    minotaureProfilInvestisseur?: ProfilInvestisseurFiche
  ): ProfilInvestisseurFiche | undefined {
    if (minotaureProfilInvestisseur?.profilQpiCode) {
      return {
        profilQpiCode: minotaureProfilInvestisseur.profilQpiCode,
        profilQpiLibelle: minotaureProfilInvestisseur.profilQpiLibelle,
        hasTolerancePerte: inv.profil?.hasTolerancePerte, //TODO: get tolerance perte :  waiting for spec for tolerance perte with new profil data: US23072 https://omnium-finance.visualstudio.com/ConseilPlacement/_workitems/edit/23072
      };
    }

    return undefined;
  }

  private static async getConnaissancClientFromInvestisseur(
    queryManager: QueryManagerService,
    inv: Investisseur
  ): Promise<ConnaissanceClientData | undefined> {
    const minotaureProfilInvestisseur = await this.getMinotaureProfilInvestisseur(queryManager, inv.id);
    return {
      situationPersonnelle: this.getInfoPersonelleFromInvestisseur(inv),
      profilInvestisseur: this.getProfilInvestisseurFromInvestisseur(inv, minotaureProfilInvestisseur),
      investisseurId: inv.id,
    };
  }

  private static async getMinotaureProfilInvestisseur(
    queryManager: QueryManagerService,
    investisseurId: number
  ): Promise<ProfilInvestisseurFiche | undefined> {
    const result = await firstValueFrom(
      queryManager.query<{ minotaureProfilInvestisseur?: ProfilInvestisseurFiche }>({
        query: GET_MINOTAURE_PROFIL_INVESTISSEUR,
        variables: { investisseurId },
        fetchPolicy: 'network-only',
      })
    );

    return result.data?.minotaureProfilInvestisseur;
  }
}
